import { BS_MONTHS_EN, adToBs, formatIsoToBs, type BsDate } from './bsDate'

/**
 * Installed host locales (Wave 5 T9, R5). `useHostLocale()` installs them once
 * host data exists; until then every formatter keeps today's defaults:
 * `en-US` dates, `en-GB` 24-hour times, `en-US` plain numbers. A derived
 * `en-IN` (Nepal, India, blank country) installs date `en-US` and time
 * `en-GB`, so Nepal strings are byte for byte what they were.
 */
export interface HostLocales {
  numberLocale: MoneyLocale | null
  dateLocale?: string | null
  timeLocale?: string | null
  homeCurrency?: string | null
  /** F-067: the host's `default_calendar` (AD, BS or BOTH). */
  calendar?: 'AD' | 'BS' | 'BOTH' | null
}

const DEFAULT_DATE_LOCALE = 'en-US'
const DEFAULT_TIME_LOCALE = 'en-GB'

let hostDateLocale: string = DEFAULT_DATE_LOCALE
let hostTimeLocale: string = DEFAULT_TIME_LOCALE
let hostCalendar: 'AD' | 'BS' | 'BOTH' = 'AD'

/** F-067: the installed host calendar preference (default AD). */
export function getHostCalendar(): 'AD' | 'BS' | 'BOTH' {
  return hostCalendar
}

/** The installed date locale (default `en-US`). */
export function getHostDateLocale(): string {
  return hostDateLocale
}

/** The installed 24-hour time locale (default `en-GB`). */
export function getHostTimeLocale(): string {
  return hostTimeLocale
}

/** Returns today as an ISO-8601 date string (YYYY-MM-DD). */
export function today(): string {
  return new Date().toISOString().slice(0, 10)
}

/**
 * Today's date in the viewer's LOCAL timezone as YYYY-MM-DD. Unlike `today()`
 * (which is UTC-based and, in UTC+ timezones like Nepal, reports the previous
 * day for the first hours after local midnight), this uses local time via
 * `toLocaleDateString('en-CA')` - the right basis for calendar comparisons such
 * as a date-input `min` or a "not in the past" guard.
 */
export function todayLocal(): string {
  return new Date().toLocaleDateString('en-CA')
}

/**
 * Returns the day after the given YYYY-MM-DD, in LOCAL time.
 *
 * Must NOT round-trip through `toISOString()`: parsing "YYYY-MM-DD" gives
 * local midnight, and in UTC+ timezones (e.g. Nepal UTC+5:45) that instant
 * is still the *previous* day in UTC - so `toISOString().slice(0,10)` would
 * silently eat the +1 and return the same date. `toLocaleDateString('en-CA')`
 * formats in local time, which is what calendar dates need.
 */
export function nextDayStr(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() + 1)
  return d.toLocaleDateString('en-CA')
}

/**
 * A server wall-clock time-of-day ("14:00:00", from a Django TimeField) as the
 * "HH:MM" the UI shows and the time pickers emit. Not a timestamp: no timezone
 * conversion happens. Returns "-" for empty input and passes anything that is
 * not an HH:MM[:SS] string through unchanged.
 */
export function fmtClock(t: string | null | undefined): string {
  if (!t) return '-'
  const m = /^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(t.trim())
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : t
}

/** Q4-BPROP8: drop empty address parts so "ramapur-03,, Palpa" reads "ramapur-03, Palpa". */
export function cleanAddress(address: string | null | undefined): string {
  return (address ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ')
}

/**
 * The 24h wall-clock time of an instant, in the viewer's LOCAL timezone
 * ("09:00"). Use this for any timestamp the server returns; never slice the
 * ISO string. The API sends UTC, so `iso.slice(11, 16)` reads back the UTC
 * clock: a 09:00 shift in Nepal (UTC+5:45) is stored as 03:15Z and renders
 * as "03:15".
 */
export function fmtTimeLocal(iso: string | null | undefined): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '-'
  return d.toLocaleTimeString(hostTimeLocale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
}

/**
 * The LOCAL calendar day (YYYY-MM-DD) an instant falls on, for grouping
 * timestamps into day columns. Same hazard as `fmtTimeLocal`, one step worse:
 * slicing the ISO string does not merely show the wrong time, it files the row
 * under the wrong day. A 02:00 shift on the 27th in Nepal is 20:15Z on the
 * 26th, so a sliced day key lands it in the previous day's column.
 */
export function localDayOf(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-CA')
}

/**
 * F-067: the two halves of a calendar-aware date label. `ad` is the AD text
 * the caller already formatted from `shown`; the Bikram Sambat date is read
 * off the same LOCAL calendar day, so both halves always name one day.
 *
 * - host calendar AD (every non-Nepal host, see useHostLocale): `{ primary:
 *   ad, secondary: null }`, so the label is unchanged;
 * - BOTH: AD leads and BS follows (`secondary` is the BS text);
 * - BS: BS leads and AD follows.
 *
 * Outside the BS table range (before BS 2000 or after BS 2090) the BS half is
 * dropped and the label falls back to AD. Display only: values stay AD.
 */
export function hostCalendarLabel(
  ad: string,
  shown: Date,
): { primary: string; secondary: string | null } {
  if (hostCalendar === 'AD' || Number.isNaN(shown.getTime()))
    return { primary: ad, secondary: null }
  const pad = (n: number) => String(n).padStart(2, '0')
  const ymd = `${shown.getFullYear()}-${pad(shown.getMonth() + 1)}-${pad(shown.getDate())}`
  const bs = formatIsoToBs(ymd)
  if (!bs) return { primary: ad, secondary: null }
  return hostCalendar === 'BS' ? { primary: bs, secondary: ad } : { primary: ad, secondary: bs }
}

/**
 * F-067: an AD date label with the host calendar rule applied, as one string:
 * "May 20, 2026 (6 Jestha 2083)" for BOTH, "6 Jestha 2083 (May 20, 2026)" for
 * BS, the AD text untouched for AD. Same rule as Finance `fmtDate`.
 */
export function withHostCalendar(ad: string, shown: Date): string {
  const { primary, secondary } = hostCalendarLabel(ad, shown)
  return secondary ? `${primary} (${secondary})` : primary
}

/**
 * "May 20, 2026" - returns '-' for null/undefined. F-067: a Nepal host whose
 * calendar is BOTH or BS also sees the Bikram Sambat date (withHostCalendar).
 */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '-'
  // A bare YYYY-MM-DD is a calendar day: read it as local midnight so a
  // negative UTC offset never shows the day before.
  const d = localDate(iso)
  const ad = d.toLocaleDateString(hostDateLocale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return withHostCalendar(ad, d)
}

/** Parses YYYY-MM-DD as LOCAL midnight (a bare ISO date parses as UTC). */
function localDate(iso: string): Date {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00`) : new Date(iso)
}

/**
 * A stay or date range in two compact lines: the year is written once,
 * after the second date, unless the range crosses a year.
 *
 * - AD: `{ primary: "Nov 1 → Nov 2, 2026", secondary: null }`
 * - BOTH: AD leads, `secondary` is "15 Kartik → 16 Kartik 2083"
 * - BS: BS leads, `secondary` is the AD range
 *
 * Outside the BS table range the BS half is dropped (AD only).
 */
export function fmtDateRange(
  fromIso: string | null | undefined,
  toIso: string | null | undefined,
): { primary: string; secondary: string | null } {
  if (!fromIso || !toIso)
    return { primary: `${fmtDate(fromIso)} → ${fmtDate(toIso)}`, secondary: null }
  const a = localDate(fromIso)
  const b = localDate(toIso)
  const md = (d: Date) => d.toLocaleDateString(hostDateLocale, { month: 'short', day: 'numeric' })
  const mdy = (d: Date) =>
    d.toLocaleDateString(hostDateLocale, { month: 'short', day: 'numeric', year: 'numeric' })
  const ad = a.getFullYear() === b.getFullYear() ? `${md(a)} → ${mdy(b)}` : `${mdy(a)} → ${mdy(b)}`
  if (hostCalendar === 'AD' || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()))
    return { primary: ad, secondary: null }
  // adToBs keys off the UTC calendar day: hand it that day at UTC midnight,
  // the same way formatIsoToBs reads a bare YYYY-MM-DD.
  const utcDay = (d: Date) => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const bsA = adToBs(utcDay(a))
  const bsB = adToBs(utcDay(b))
  if (!bsA || !bsB) return { primary: ad, secondary: null }
  const bsDm = (x: BsDate) => `${x.day} ${BS_MONTHS_EN[x.month - 1]}`
  const bs =
    bsA.year === bsB.year
      ? `${bsDm(bsA)} → ${bsDm(bsB)} ${bsB.year}`
      : `${bsDm(bsA)} ${bsA.year} → ${bsDm(bsB)} ${bsB.year}`
  return hostCalendar === 'BS' ? { primary: bs, secondary: ad } : { primary: ad, secondary: bs }
}

/**
 * UX-07: "Oct 9" in the host date locale ("9 Oct" for a day-first locale), for
 * chart axes and compact lists. Replaces raw "10-09" slices, which read as
 * 10 September to day-first readers.
 */
export function fmtDayMonth(iso: string | null | undefined): string {
  if (!iso) return '-'
  return localDate(iso).toLocaleDateString(hostDateLocale, { month: 'short', day: 'numeric' })
}

/** UX-07: "Friday, October 9, 2026" in the host date locale (page date lines). */
export function fmtDateLong(iso: string | null | undefined): string {
  if (!iso) return '-'
  return localDate(iso).toLocaleDateString(hostDateLocale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/** UX-07: "Friday, Oct 2" in the host date locale. */
export function fmtWeekdayDayMonth(iso: string | null | undefined): string {
  if (!iso) return '-'
  return localDate(iso).toLocaleDateString(hostDateLocale, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  })
}

/** "Tue, May 20" (no year) - for compact date labels. */
export function fmtDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString(hostDateLocale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Date and time on two lines for compact tables: `primary` is
 * "Oct 31, 2026 · 07:06 PM", `secondary` the other calendar's date
 * ("14 Kartik 2083" for a BOTH host; the AD date for a BS host) or null.
 */
export function fmtDateTimeParts(iso: string | null | undefined): {
  primary: string
  secondary: string | null
} {
  if (!iso) return { primary: '-', secondary: null }
  const d = new Date(iso)
  const ad = d.toLocaleDateString(hostDateLocale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const time = d.toLocaleTimeString(hostDateLocale, { hour: '2-digit', minute: '2-digit' })
  const { primary, secondary } = hostCalendarLabel(ad, d)
  return { primary: `${primary} · ${time}`, secondary }
}

/** "May 20, 2026 · 10:30 AM" - returns '-' for null/undefined; BS rule as fmtDate. */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '-'
  const d = new Date(iso)
  const ad = d.toLocaleDateString(hostDateLocale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return `${withHostCalendar(ad, d)} · ${d.toLocaleTimeString(hostDateLocale, { hour: '2-digit', minute: '2-digit' })}`
}

// ─── Money: the one formatter ──────────────────────────────────────────────
//
// Task 19: every money string in the app goes through `fmtMoney`. The rules:
//   * the currency CODE always leads ("NPR 1,500.00"), never a symbol;
//   * minor units come from Intl per ISO-4217 code (JPY/KRW/VND have none),
//     unless the caller forces `decimals`;
//   * digit grouping comes from the HOST locale: `en-IN` (lakh/crore,
//     "12,34,567.00", "12.3L") only for Nepal and India hosts, any other
//     supported locale through Intl ("1.234,56" for de-DE), and `en-US`
//     (thousands, "1,234,567.00", "1.2M") when none is installed;
//   * one negative style: a minus sign after the code ("NPR -1,500.00").
//     Only the trial balance may opt into `accounting: true` ("NPR (1,500.00)").

/** Number locale used for digit grouping. */
export type MoneyLocale =
  | 'en-US'
  | 'en-GB'
  | 'en-IN'
  | 'en-AU'
  | 'en-CA'
  | 'en-NZ'
  | 'en-SG'
  | 'en-AE'
  | 'de-DE'
  | 'fr-FR'
  | 'es-ES'
  | 'it-IT'
  | 'nl-NL'
  | 'pt-PT'
  | 'ja-JP'
  | 'th-TH'

// Lakh/crore grouping ("L"/"Cr") is a South-Asian convention. When no host
// locale is known (platform-admin app, tests) compact output falls back to
// grouping by the currency's own market.
export const LAKH_CRORE_CODES = new Set(['NPR', 'INR', 'PKR', 'LKR', 'BDT'])

// Blank country means Nepal (PMSHostDB.country contract).
const LAKH_COUNTRIES = new Set(['NP', 'IN', ''])

/** `en-IN` for Nepal and India hosts (and a blank country), `en-US` otherwise. */
export function localeForCountry(country: string | null | undefined): MoneyLocale {
  return LAKH_COUNTRIES.has((country ?? '').trim().toUpperCase()) ? 'en-IN' : 'en-US'
}

let hostMoneyLocale: MoneyLocale | null = null
let hostHomeCurrency: string | null = null

/**
 * Set the signed-in host's grouping locale (and home currency) for every
 * later `fmtMoney` call. Called by `useHostLocale()` in the host shell once
 * host data exists; `null` clears it (tests, sign-out). Outside a host
 * session full amounts group in thousands.
 */
export function setHostLocale(locales: HostLocales): void {
  hostMoneyLocale = locales.numberLocale
  hostHomeCurrency = locales.homeCurrency ?? null
  hostDateLocale = locales.dateLocale || DEFAULT_DATE_LOCALE
  hostTimeLocale = locales.timeLocale || DEFAULT_TIME_LOCALE
  hostCalendar = locales.calendar || 'AD'
}

/** Clear every installed host locale back to the defaults (sign-out, host switch). */
export function resetHostLocale(): void {
  setHostLocale({ numberLocale: null })
}

/**
 * Deprecated wrapper kept for one release: installs the grouping locale and
 * home currency only, and resets dates and times to today's defaults.
 */
export function setHostMoneyLocale(locale: MoneyLocale | null, homeCurrency?: string | null): void {
  setHostLocale({ numberLocale: locale, homeCurrency })
}

/** The currency's own market grouping: lakh/crore codes en-IN, the rest en-US. */
export function marketLocale(currency: string): MoneyLocale {
  return LAKH_CRORE_CODES.has(currency) ? 'en-IN' : 'en-US'
}

/**
 * Which grouping a currency gets. Ruling (task 19 review): the host locale
 * applies to the host's home currency (and to every currency when the home
 * is unknown and the currency market agrees); any other currency groups by
 * its own market, so USD reads "1,234,567.00" and "1.2M" even on a Nepal
 * host, while NPR/INR/PKR/LKR/BDT read in lakh and crore.
 */
function resolveLocale(currency: string, explicit: MoneyLocale | undefined): MoneyLocale | null {
  if (explicit) return explicit
  if (!hostMoneyLocale) return null
  if (
    hostHomeCurrency ? currency === hostHomeCurrency : marketLocale(currency) === hostMoneyLocale
  ) {
    return hostMoneyLocale
  }
  return marketLocale(currency)
}

/** The host locale currently in force, or null when none is set. */
export function getHostMoneyLocale(): MoneyLocale | null {
  return hostMoneyLocale
}

export interface FmtMoneyOptions {
  /** Force this many fraction digits (default: the currency's minor units). */
  decimals?: number
  /** "NPR 12.3L" / "USD 1.2M". Sub-thousand amounts print whole. */
  compact?: boolean
  /** Negatives in parentheses. Trial balance only. */
  accounting?: boolean
  /** Override the grouping locale (default: the host locale). */
  locale?: MoneyLocale
}

const minorUnitCache = new Map<string, number>()

/** ISO-4217 minor units per Intl (JPY 0, USD 2, KWD 3); 2 for unknown codes. */
export function minorUnits(currency: string): number {
  const cached = minorUnitCache.get(currency)
  if (cached !== undefined) return cached
  let digits = 2
  try {
    digits =
      new Intl.NumberFormat('en-US', { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
  } catch {
    // Unknown code: keep 2, never throw in the UI.
  }
  minorUnitCache.set(currency, digits)
  return digits
}

const nfCache = new Map<string, Intl.NumberFormat>()

function numberFormatter(locale: MoneyLocale, decimals: number): Intl.NumberFormat {
  const key = `${locale}|${decimals}`
  let nf = nfCache.get(key)
  if (!nf) {
    nf = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
    nfCache.set(key, nf)
  }
  return nf
}

/**
 * The compact magnitude of a non-negative `amount`, no currency prefix.
 * `lakh` picks L/Cr over M/B; it defaults to the currency's market.
 */
export function compactMagnitude(
  amount: number,
  currencyCode: string,
  lakh?: boolean,
  locale?: MoneyLocale | null,
): string {
  const useLakh = lakh ?? LAKH_CRORE_CODES.has(currencyCode)
  if (useLakh) {
    // Beyond 9,999 Cr the compact form would print five or more digits,
    // which is no longer compact: print the full grouped amount instead.
    if (amount >= 10_000_000 * 10_000) return numberFormatter('en-IN', 0).format(amount)
    if (amount >= 10_000_000) return `${(amount / 10_000_000).toFixed(2)}Cr`
    if (amount >= 100_000) return `${(amount / 100_000).toFixed(1)}L`
    if (amount >= 1_000) return `${(amount / 1_000).toFixed(1)}K`
  } else {
    if (amount >= 1_000_000_000_000) return `${(amount / 1_000_000_000_000).toFixed(2)}T`
    if (amount >= 1_000_000_000) return `${(amount / 1_000_000_000).toFixed(2)}B`
    if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M`
    if (amount >= 1_000) return `${(amount / 1_000).toFixed(1)}K`
  }
  return numberFormatter(useLakh ? 'en-IN' : (locale ?? 'en-US'), 0).format(amount)
}

/**
 * The one money formatter: "NPR 1,500.00", "JPY 1,500", "NPR 12,34,567.00"
 * (Nepal host), "USD 1.2M" (compact), "NPR -1,500.00" (negative),
 * "NPR (1,500.00)" (accounting). Returns '-' for null/undefined/NaN.
 *
 * `currency` is REQUIRED: the folio currency for folio/booking money, or
 * `useHostCurrencies().home` for property-wide figures. The third argument
 * is the options object; a bare number is still accepted as `decimals`.
 */
export function fmtMoney(
  val: string | number | null | undefined,
  currency: string,
  options?: number | FmtMoneyOptions,
): string {
  if (val === null || val === undefined) return '-'
  // Strip grouping commas first: parseFloat("1,234.50") would read 1.
  const n = typeof val === 'string' ? parseFloat(val.replace(/,/g, '')) : val
  if (typeof n !== 'number' || isNaN(n)) return '-'
  const opts: FmtMoneyOptions =
    typeof options === 'number' ? { decimals: options } : (options ?? {})
  const abs = Math.abs(n)
  let body: string
  if (opts.compact) {
    const locale = resolveLocale(currency, opts.locale)
    body = compactMagnitude(abs, currency, locale ? locale === 'en-IN' : undefined, locale)
  } else {
    const locale = resolveLocale(currency, opts.locale) ?? 'en-US'
    body = numberFormatter(locale, opts.decimals ?? minorUnits(currency)).format(abs)
  }
  // A value that rounds to zero is not negative: never print "-0.00".
  const negative = n < 0 && /[1-9]/.test(body)
  if (!negative) return `${currency} ${body}`
  return opts.accounting ? `${currency} (${body})` : `${currency} -${body}`
}

/**
 * A price as a person types it: "NPR 4,000" when whole, "USD 12.50" when
 * not. For rate and rule labels; ledgers and folios use `fmtMoney`.
 */
export function fmtPrice(val: string | number | null | undefined, currency: string): string {
  const n = typeof val === 'string' ? parseFloat(val.replace(/,/g, '')) : val
  return fmtMoney(val, currency, typeof n === 'number' && Number.isInteger(n) ? 0 : undefined)
}

/**
 * `fmtMoney` without the leading code, for columns whose header already
 * names the currency once. Same grouping, minor units and sign rules.
 */
export function fmtMoneyNumber(
  val: string | number | null | undefined,
  currency: string,
  options?: number | FmtMoneyOptions,
): string {
  const s = fmtMoney(val, currency, options)
  return s === '-' ? s : s.slice(currency.length + 1)
}

/**
 * Grouped number with NO currency code, for tables/columns that already show
 * the currency once in a header. Replaces the fragile `fmtMoney(x).replace('NPR ', '')`
 * idiom, which broke the moment the home currency was not NPR.
 */
export function fmtNumber(val: string | number | null | undefined, decimals = 2): string {
  if (val === null || val === undefined) return '-'
  const n = typeof val === 'string' ? parseFloat(val) : val
  if (isNaN(n)) return '-'
  // Plain numbers never take lakh grouping (today's behaviour on a Nepal host):
  // `en-IN` falls back to `en-US`, any other installed locale applies.
  const locale = hostMoneyLocale && hostMoneyLocale !== 'en-IN' ? hostMoneyLocale : 'en-US'
  return n.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

/**
 * "NPR 20.0L" / "USD 1.2M": compact money for dashboard KPIs. Delegates to
 * `fmtMoney(val, currency, { compact: true })`.
 */
export function fmtShortNum(val: string | number, currency: string): string {
  return fmtMoney(val, currency, { compact: true })
}

/** "13.0%" - multiplies by 100 first (input is a decimal fraction). */
export function fmtPct(v: string | number): string {
  return `${(Number(v ?? 0) * 100).toFixed(1)}%`
}

/** FO-24: "1 night", "3 nights". */
export function nightsLabel(n: number): string {
  return `${n} night${n === 1 ? '' : 's'}`
}

/** FO-24: end a sentence on a name without doubling its own full stop ("Pvt. Ltd."). */
export function endSentence(text: string): string {
  const t = text.trimEnd()
  return /[.!?]$/.test(t) ? t : `${t}.`
}

/** Number of nights between two ISO-8601 date strings. */
export function countNights(from: string, to: string): number {
  return Math.max(0, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86_400_000))
}
